import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { GameProvider } from './features/conectados';
import { LanguageProvider } from './shared/i18n';
import { LanguageSwitcher, ScrollToTop } from './shared/components';
import logo from './assets/images/fe-en-juego_logo-large.png';
import useDocumentTitle from './shared/hooks/useDocumentTitle';

const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    border-radius: 6px;
    transition: all 0.2s ease;
  }

  button:hover {
    transform: translateY(-1px);
  }

  button:active {
    transform: translateY(0);
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    body {
      font-size: 14px;
    }
  }
`;

const AppContainer = styled.div`
  text-align: center;
  min-height: 100vh;
  background: white;
`;

const Header = styled.header`
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  gap: 1rem;
  min-height: 90px;

  @media (max-width: 768px) {
    gap: 0.5rem;
    padding: 0 1rem;
    min-height: 75px;
  }
`;

const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    position: relative;
    left: auto;
    transform: none;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-left: auto;
  }
`;

const LogoContainer = styled.div`
  cursor: pointer;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;

  &:active {
    transform: scale(0.98);
  }
`;

const Logo = styled.img`
  height: 90px;
  width: auto;

  @media (max-width: 768px) {
    height: 75px;
  }
`;

const MainContent = styled.main`
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  animation: fadeIn 0.3s ease-in;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

function HeaderContent() {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <Header>
      <div style={{ width: '120px', visibility: 'hidden' }} /> {/* Spacer for layout balance */}
      <HeaderCenter>
        <LogoContainer onClick={handleLogoClick}>
          <Logo src={logo} alt="Fe en Juego Logo" />
        </LogoContainer>
      </HeaderCenter>
      <HeaderRight>
        <LanguageSwitcher showNames={false} />
      </HeaderRight>
    </Header>
  );
}

function App() {
  useDocumentTitle();
  const location = useLocation();
  
  return (
    <LanguageProvider>
      <GameProvider>
        <GlobalStyle />
        <ScrollToTop />
        <AppContainer>
          <HeaderContent />
          <MainContent>
            <AnimatePresence mode="wait">
              <Outlet key={location.pathname} />
            </AnimatePresence>
          </MainContent>
        </AppContainer>
      </GameProvider>
    </LanguageProvider>
  );
}

export default App;
